import {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { io } from "socket.io-client";
import { useAuth } from "../Auth/AuthContext";

// One Socket.IO connection for the whole app, established for admins only (the
// gateway rejects non-admins on the handshake anyway). Same-origin: in prod the
// backend serves us; in dev Next proxies /socket.io. The session cookie
// authenticates the handshake.
const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
	const { isAdmin } = useAuth();
	const [socket, setSocket] = useState(null);

	useEffect(() => {
		if (!isAdmin) return undefined;
		const s = io({ withCredentials: true, path: "/socket.io" });
		setSocket(s);
		return () => {
			s.close();
			setSocket(null);
		};
	}, [isAdmin]);

	return (
		<RealtimeContext.Provider value={socket}>
			{children}
		</RealtimeContext.Provider>
	);
}

export function useRealtime() {
	return useContext(RealtimeContext);
}

/**
 * Subscribes to turn events; `handler(event, payload)` is called on every
 * `turn:checkedin`, `turn:called` and `turn:finalized`. The handler ref is kept
 * current so callers don't need to memoize it.
 */
export function useTurnEvents(handler) {
	const socket = useRealtime();
	const ref = useRef(handler);
	ref.current = handler;

	useEffect(() => {
		if (!socket) return undefined;
		const onCheckedin = (p) => ref.current?.("turn:checkedin", p);
		const onCalled = (p) => ref.current?.("turn:called", p);
		const onFinalized = (p) => ref.current?.("turn:finalized", p);
		socket.on("turn:checkedin", onCheckedin);
		socket.on("turn:called", onCalled);
		socket.on("turn:finalized", onFinalized);
		return () => {
			socket.off("turn:checkedin", onCheckedin);
			socket.off("turn:called", onCalled);
			socket.off("turn:finalized", onFinalized);
		};
	}, [socket]);
}
